import React, { useState } from 'react';
import { CompleteKundali, KundaliChartData, Language, PlanetPosition } from '../types/kundali';
import { getEnhancedPlanetaryDignityList, EnhancedPlanetaryDignity } from '../utils/planetaryDignityEngine';
import { getPlanetFullName, getPlanetShortCode, getZodiacName, getNakshatraName } from '../utils/language';
import {
  Sparkles,
  Shield,
  Activity,
  Award,
  Zap,
  Flame,
  Info,
  ChevronRight,
  TrendingUp,
  BarChart3,
  SlidersHorizontal,
  Compass,
  Star,
  Eye,
  EyeOff
} from 'lucide-react';
import { PopoverWrapper } from './PlanetaryHoverPopover';
import { DetailedPlanetaryPopoverContent } from './DetailedPlanetaryPopoverContent';

interface PlanetaryShadbalaMatrixViewProps {
  kundali: CompleteKundali;
  chartData: KundaliChartData;
  selectedHouseNumber?: number;
  selectedPlanet?: PlanetPosition | null;
  onSelectPlanet?: (planet: PlanetPosition | null) => void;
  lang?: Language;
}

export const PlanetaryShadbalaMatrixView: React.FC<PlanetaryShadbalaMatrixViewProps> = ({
  kundali,
  chartData,
  selectedHouseNumber,
  selectedPlanet,
  onSelectPlanet,
  lang = 'en'
}) => {
  const isHi = lang === 'hi';
  const activeLang = lang;
  const [activeFilter, setActiveFilter] = useState<'all' | 'benefics' | 'malefics' | 'strong' | 'afflicted'>('all');
  const [hoverInspectorEnabled, setHoverInspectorEnabled] = useState<boolean>(true);

  // Compute rich dignity and shadbala matrix for all planets in chart
  const matrixData: EnhancedPlanetaryDignity[] = getEnhancedPlanetaryDignityList(kundali, lang);

  const filteredMatrix = matrixData.filter(item => {
    if (activeFilter === 'benefics') return item.functionalNature.includes('Benefic') || item.isYogakaraka;
    if (activeFilter === 'malefics') return item.functionalNature.includes('Malefic');
    if (activeFilter === 'strong') return (item.shadbala?.ratio ?? 1) >= 1.0 || item.isExalted || item.dignity === 'Own House';
    if (activeFilter === 'afflicted') return item.isDebilitated || (item.shadbala?.ratio ?? 1) < 1.0;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Header Bar with View Sub-title & Quick Filters */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-xl bg-astro-primary/10 text-astro-primary flex items-center justify-center font-bold">
            <BarChart3 className="w-4 h-4 text-astro-primary" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm sm:text-base font-bold text-astro-primary font-serif">
                {isHi ? 'ग्रह षड्बल एवं गरिमा सारणी' : 'Planetary & Shadbala Matrix'}
              </h3>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-astro-teal-tint text-astro-primary border border-astro-primary/20">
                {isHi ? 'समग्र ९ ग्रह विश्लेषण' : 'All 9 Grahas Comprehensive'}
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text">
              {isHi
                ? '६-स्तरीय बल (षड्बल अनुपात), रूपा बल, संबंध, उच्च/नीच व कार्यात्मक प्रकृति का त्वरित मैट्रिक्स'
                : '6-fold Shadbala potencies, Rupa scores, Digbala, Uchhabala, Saptavargiya dignity & functional roles'}
            </p>
          </div>
        </div>

        {/* Controls: Hover Toggle + Category Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setHoverInspectorEnabled(!hoverInspectorEnabled)}
            className={`px-2.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border transition-all cursor-pointer shadow-2xs ${
              hoverInspectorEnabled
                ? 'bg-astro-primary text-white border-astro-primary'
                : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
            }`}
            title={isHi ? 'होवर पॉपओवर सक्षम/अक्षम करें' : 'Toggle rich hover tooltips'}
          >
            {hoverInspectorEnabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{hoverInspectorEnabled ? (isHi ? 'होवर: चालू' : 'Hover: ON') : (isHi ? 'होवर: बंद' : 'Hover: OFF')}</span>
          </button>

          <div className="flex bg-astro-card p-1 rounded-xl border border-astro-border flex-wrap gap-1">
            {[
              { id: 'all', label: isHi ? 'सभी' : 'All' },
              { id: 'benefics', label: isHi ? 'शुभ' : 'Benefics' },
              { id: 'malefics', label: isHi ? 'अशुभ' : 'Malefics' },
              { id: 'strong', label: isHi ? 'बली (≥1.0)' : 'Strong (≥1.0)' },
              { id: 'afflicted', label: isHi ? 'कमजोर/नीच' : 'Low (<1.0)' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id as any)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                  activeFilter === f.id
                    ? 'bg-astro-primary text-white shadow-2xs'
                    : 'text-astro-secondary-text hover:text-astro-text'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* High Density Analytical Table */}
      <div className="bg-white border border-astro-border rounded-2xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-[#FAF6ED] text-[#263633] font-bold border-b border-astro-border">
                <th className="py-3 px-3.5 font-serif">{isHi ? 'ग्रह' : 'Planet'}</th>
                <th className="py-3 px-3 font-serif">{isHi ? 'राशि व भाव' : 'Sign & House'}</th>
                <th className="py-3 px-3 font-serif">{isHi ? 'गरिमा (Dignity)' : 'Dignity'}</th>
                <th className="py-3 px-3 font-serif">{isHi ? 'षड्बल अनुपात' : 'Shadbala Ratio'}</th>
                <th className="py-3 px-3 font-serif">{isHi ? 'रूपा बल' : 'Rupa Points'}</th>
                <th className="py-3 px-3 font-serif">{isHi ? 'नक्षत्र' : 'Nakshatra'}</th>
                <th className="py-3 px-3 font-serif">{isHi ? 'कार्यात्मक प्रकृति' : 'Functional Nature'}</th>
                <th className="py-3 px-3 font-serif text-right">{isHi ? 'स्थिति' : 'Verdict'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-astro-border/60">
              {filteredMatrix.map(item => {
                const pos = chartData.planets?.find(p => p.name === item.planet);
                const isSelected = selectedPlanet?.name === item.planet;
                const ratio = item.shadbala?.ratio ?? 1;
                const isAdequate = ratio >= 1.0;

                const tableRowContent = (
                  <tr
                    key={item.planet}
                    onClick={() => pos && onSelectPlanet?.(pos)}
                    className={`hover:bg-astro-card/80 transition-colors cursor-pointer ${
                      isSelected ? 'bg-astro-primary/5 font-semibold' : ''
                    }`}
                  >
                    {/* Planet Identifier */}
                    <td className="py-3 px-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-lg bg-astro-primary text-white font-bold flex items-center justify-center text-xs shadow-2xs">
                          {getPlanetShortCode(item.planet, activeLang)}
                        </span>
                        <div>
                          <span className="font-bold text-astro-primary block">
                            {getPlanetFullName(item.planet, activeLang)}
                          </span>
                          <span className="text-[10px] text-astro-secondary-text font-mono">
                            {item.formattedDegree} {pos?.isRetrograde ? (isHi ? '(वक्री)' : '(R)') : ''}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Sign & House */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="font-bold text-astro-primary">
                          {getZodiacName(item.sign, activeLang)}
                        </span>
                        <span className="text-[10px] text-astro-secondary-text">
                          {isHi ? `भाव ${item.house}` : `House ${item.house}`}
                        </span>
                      </div>
                    </td>

                    {/* Dignity */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                        item.isExalted ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                        item.isDebilitated ? 'bg-red-100 text-red-800 border border-red-300' :
                        item.dignity === 'Own House' ? 'bg-teal-100 text-teal-800 border border-teal-300' :
                        'bg-astro-card text-astro-text border border-astro-border'
                      }`}>
                        {isHi ? item.dignityLabelHi : item.dignityLabelEn}
                      </span>
                    </td>

                    {/* Shadbala Ratio Bar */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-[#E2DED5] h-2 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              isAdequate ? 'bg-emerald-600' : 'bg-amber-500'
                            }`}
                            style={{ width: `${Math.min(100, Math.max(10, ratio * 75))}%` }}
                          />
                        </div>
                        <span className={`font-mono text-xs font-bold ${isAdequate ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {ratio.toFixed(2)}x
                        </span>
                      </div>
                    </td>

                    {/* Rupa */}
                    <td className="py-3 px-3 whitespace-nowrap font-mono text-xs text-astro-primary font-bold">
                      {item.shadbala ? `${item.shadbala.totalVirupas.toFixed(0)} vir` : '—'}
                    </td>

                    {/* Nakshatra */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-astro-primary font-medium">
                          {getNakshatraName(item.nakshatraImpact.nakshatra, activeLang)}
                        </span>
                        <span className="text-[10px] text-astro-secondary-text">
                          {isHi ? `पद ${item.nakshatraImpact.pada}` : `Pada ${item.nakshatraImpact.pada}`} • {item.nakshatraImpact.lord}
                        </span>
                      </div>
                    </td>

                    {/* Functional Role */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className={`text-[10.5px] font-bold ${
                        item.isYogakaraka ? 'text-purple-700' :
                        item.functionalNature.includes('Benefic') ? 'text-emerald-700' :
                        item.functionalNature.includes('Malefic') ? 'text-red-700' :
                        'text-astro-secondary-text'
                      }`}>
                        {isHi ? item.functionalNatureHi : item.functionalNature}
                      </span>
                    </td>

                    {/* Verdict Icon */}
                    <td className="py-3 px-3 whitespace-nowrap text-right">
                      {isAdequate ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                          ✓ {isHi ? 'सामर्थ्यवान' : 'Potent'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                          ! {isHi ? 'उपाय योग्य' : 'Remedial'}
                        </span>
                      )}
                    </td>
                  </tr>
                );

                if (hoverInspectorEnabled) {
                  return (
                    <PopoverWrapper
                      key={item.planet}
                      enabled={hoverInspectorEnabled}
                      trigger={tableRowContent}
                      content={<DetailedPlanetaryPopoverContent dignity={item} lang={lang} />}
                    />
                  );
                }

                return tableRowContent;
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
